import Container from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';

import { DecorativeLines } from '@/components/ui/decorative-lines';
import { Image } from '@/components/ui/image/image';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { cn } from '@/lib/utils';
import type { SectionBgVariant } from '@/lib/types/misc';
import type { Document } from '@contentful/rich-text-types';
import type { HTMLAttributes, ReactNode } from 'react';
import { AgendaCard, type AgendaCardData } from './agenda-card';
import { AgendaCarousel } from './agenda-carousel';
import styles from './agenda-section.module.scss';

export type AgendaSectionProps = {
  sectionId: string;
  badge?: string;

  title: string;
  description?: Document;
  cta?: ReactNode;
  cards: AgendaCardData[];
  enableAutoplay?: boolean;
  controls?: boolean;
  autoscrollSpeed?: number;
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string | null;
  backgroundColorFrom?: string;
  isBackgroundDark?: boolean;
  showDecorativeLines?: boolean;
  showDecorativeDots?: boolean;
  className?: string;
} & HTMLAttributes<HTMLElement>;

export const AgendaSection = ({
  sectionId,
  badge,

  title,
  description,
  cta,
  cards,
  enableAutoplay = false,
  controls = true,
  autoscrollSpeed,
  backgroundVariant = 'white',
  backgroundImage,
  backgroundColorFrom,
  isBackgroundDark = false,
  showDecorativeLines = false,
  showDecorativeDots = false,
  className,
  ...props
}: AgendaSectionProps) => {
  const isDark = isBackgroundDark;
  const useCarousel = cards.length > 4;

  const backgroundStyle =
    backgroundVariant === 'purple-gradient'
      ? {
          background:
            'linear-gradient(90deg, #36136A 0%, #3C2877 29%, #444187 64%, #474C8E 81%, #4B5996 100%)',
        }
      : backgroundVariant === 'purple-gradient-vertical'
        ? { background: 'linear-gradient(180deg, #36146a 0%, #413079 100%)' }
        : backgroundVariant === 'custom' && backgroundColorFrom
          ? { backgroundColor: backgroundColorFrom }
          : undefined;

  return (
    <section
      id={sectionId}
      className={cn(styles.section, styles[`section--bg-${backgroundVariant}`], className)}
      style={backgroundStyle}
      {...props}
    >
      {(showDecorativeLines || showDecorativeDots) && (
        <DecorativeLines
          showDots={showDecorativeDots}
          showLines={showDecorativeLines}
          variant={isDark ? 'dark' : 'light'}
        />
      )}

      {backgroundVariant === 'image' && backgroundImage && (
        <div className={styles.section__background}>
          <Image
            src={backgroundImage}
            alt=""
            aria-hidden
            className={styles.section__background_image}
            fill
          />
        </div>
      )}

      <Container>
        <div className={styles.inner}>
          <div className={styles.header}>
            {badge && <Badge text={badge} />}
            <div>
              <h2
                className={cn(
                  styles.header__heading,
                  isDark ? styles['header__heading--dark'] : styles['header__heading--light'],
                )}
              >
                {title}
              </h2>
              {description && (
                <div
                  className={cn(
                    styles.header__description,
                    isDark
                      ? styles['header__description--dark']
                      : styles['header__description--light'],
                  )}
                >
                  <RichTextViewer content={description} />
                </div>
              )}
            </div>
            {cta && <div className={styles.header__cta}>{cta}</div>}
          </div>

          {useCarousel ? (
            <AgendaCarousel
              cards={cards}
              enableAutoplay={enableAutoplay}
              controls={controls}
              autoscrollSpeed={autoscrollSpeed}
            />
          ) : (
            <div
              className={cn(
                styles.cards_grid,
                cards.length === 4 && styles['cards_grid--four'],
                cards.length < 3 && styles['cards_grid--centered'],
              )}
            >
              {cards.map((card, i) => (
                <AgendaCard key={`${card.id}-${i}`} {...card} />
              ))}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};
